import claudeLogo from "/claude_logo.png";
import claudeCodeLogo from "/claude_code_logo.png";
import openaiLogo from "/openai_logo.png";
import mistralLogo from "/mistral_logo.png";
import gooseLogoDark from "/goose_logo_dark.png";
import gooseLogoLight from "/goose_logo_light.png";
import cursorLogo from "/cursor_logo.png";
import copilotLogo from "/copilot_logo.png";
import codexLogo from "/codex-logo.svg";
import vscodeLogo from "/vscode_logo.svg";
import bedrockLogo from "/bedrock_logo.svg";
import n8nLogo from "/n8n_logo.svg";
import perplexityLogo from "/perplexity_logo.svg";
import clineLogoDark from "/cline_logo_dark.svg";
import clineLogoLight from "/cline_logo_light.svg";
import notionLogo from "/notion_logo.png";
import slackLogo from "/slack_logo.png";
import sphinxLogo from "/sphinx.svg";
import { UIType } from "@/lib/mcp-ui/mcp-apps-utils";
import {
  CHATGPT_CHAT_BACKGROUND,
  CHATGPT_FONT_CSS,
  CHATGPT_PLATFORM,
  getChatGPTStyleVariables,
} from "@/config/chatgpt-client-context";
import {
  CLAUDE_DESKTOP_CHAT_BACKGROUND,
  CLAUDE_DESKTOP_FONT_CSS,
  CLAUDE_DESKTOP_PLATFORM,
  getClaudeDesktopStyleVariables,
} from "@/config/claude-desktop-client-context";
import {
  CURSOR_CHAT_BACKGROUND,
  CURSOR_FONT_CSS,
  CURSOR_PLATFORM,
  getCursorStyleVariables,
} from "@/config/cursor-client-context";
import {
  VSCODE_CHAT_BACKGROUND,
  VSCODE_FONT_CSS,
  VSCODE_PLATFORM,
  getVscodeStyleVariables,
} from "@/config/vscode-client-context";
import {
  SPHINX_CHAT_BACKGROUND,
  SPHINX_FONT_CSS,
  SPHINX_PLATFORM,
  getSphinxStyleVariables,
} from "@/config/sphinx-client-context";
import {
  MISTRAL_CHAT_BACKGROUND,
  MISTRAL_FONT_CSS,
  MISTRAL_PLATFORM,
  getMistralStyleVariables,
} from "@/config/mistral-client-context";
import {
  GOOSE_CHAT_BACKGROUND,
  GOOSE_FONT_CSS,
  GOOSE_PLATFORM,
  getGooseStyleVariables,
} from "@/config/goose-client-context";
import {
  SLACK_CHAT_BACKGROUND,
  SLACK_FONT_CSS,
  SLACK_PLATFORM,
  getSlackStyleVariables,
} from "@/config/slack-client-context";
import { ClaudeMarkIndicator } from "./indicators/claude-mark";
import { ClaudeCodeCliIndicator } from "./indicators/claude-code-cli";
import { ChatGptDotIndicator } from "./indicators/chatgpt-dot";
import { GooseIconIndicator } from "./indicators/goose-icon";
import { CursorShineIndicator } from "./indicators/cursor-shine";
import { CopilotPulseIndicator } from "./indicators/copilot-pulse";
import { CodexShineIndicator } from "./indicators/codex-shine";
import { SphinxMarkIndicator } from "./indicators/sphinx-mark";
import { N8nMarkIndicator } from "./indicators/n8n-mark";
import { PerplexityMarkIndicator } from "./indicators/perplexity-mark";
import { ClineMarkIndicator } from "./indicators/cline-mark";
import { NotionShimmerIndicator } from "./indicators/notion-shimmer";
import { MistralSpinnerIndicator } from "./indicators/mistral-spinner";
import type {
  HostStyleDefinition,
  ResolvedMcpAppsCapabilities,
  ResolvedOpenAiAppsCapabilities,
} from "./types";
// Canonical MCP Apps capability matrices live in the SDK so the compat engine
// and this playground emulation share ONE source. The SDK types them sparse
// (`McpAppsCapabilities`); they are complete surfaces, so we present them as
// the client's resolved (all-required) shape. The cast is guarded by a
// completeness test (built-ins matrix-parity). SLACK + the OpenAI surfaces stay
// local — the SDK catalog doesn't carry them.
import {
  MCP_APPS_FULL,
  MCP_APPS_CLAUDE,
  MCP_APPS_CHATGPT,
  MCP_APPS_COPILOT,
  MCP_APPS_CURSOR,
  MCP_APPS_GOOSE,
  MCP_APPS_NO_CLAIMS,
  MCP_APPS_VSCODE,
} from "@sphinx/sdk/host-compat";

/**
 * Full `window.openai.*` method surface — every method on, every display
 * mode allowed. This is what ChatGPT (the original Apps SDK host) and the
 * Sphinx dev shim advertise.
 *
 * `selectFiles` is `true` here for type completeness and forward
 * compatibility, but the SDK runtime in
 * `sdk/src/McpAppsOpenAICompatibleRuntime.ts` does NOT install it yet —
 * widgets that feature-detect on it must see `typeof
 * window.openai.selectFiles === "undefined"` to take their fallback path.
 * `setOpenInAppUrl` is implemented by the runtime and host fullscreen
 * chrome.
 */
export const OPENAI_APPS_FULL_SURFACE: ResolvedOpenAiAppsCapabilities = {
  callTool: true,
  sendFollowUpMessage: true,
  setWidgetState: true,
  requestDisplayMode: "all",
  notifyIntrinsicHeight: true,
  openExternal: true,
  setOpenInAppUrl: true,
  requestModal: true,
  uploadFile: true,
  selectFiles: true,
  getFileDownloadUrl: true,
  requestCheckout: true,
  requestClose: true,
};

/**
 * Microsoft 365 Copilot's published per-method surface, verbatim from
 * the "Supported MCP Apps capabilities in Copilot" → "Component bridge"
 * table at
 * https://learn.microsoft.com/en-us/microsoft-365/copilot/extensibility/plugin-mcp-apps
 *
 * Diffs from FULL: `requestDisplayMode` is fullscreen-only;
 * `requestModal`, `uploadFile`, `getFileDownloadUrl`, `requestCheckout`,
 * `selectFiles` are off. Everything else is on.
 */
export const OPENAI_APPS_COPILOT_SURFACE: ResolvedOpenAiAppsCapabilities = {
  callTool: true,
  sendFollowUpMessage: true,
  setWidgetState: true,
  requestDisplayMode: "fullscreen-only",
  notifyIntrinsicHeight: true,
  openExternal: true,
  setOpenInAppUrl: true,
  requestModal: false,
  uploadFile: false,
  selectFiles: false,
  getFileDownloadUrl: false,
  requestCheckout: false,
  requestClose: true,
};

/**
 * Full MCP Apps `app.*` spec-bridge surface — every spec dimension on,
 * every display mode allowed. Used by Claude / ChatGPT / Cursor / Codex /
 * Sphinx as the per-host baseline before per-preset overrides
 * (`hostCapabilitiesAugment`, sparser `mcpAppsCapabilities` keys) tighten
 * specific rows.
 *
 * Independent from {@link OPENAI_APPS_FULL_SURFACE} — the two surfaces
 * model different APIs (`window.openai.*` shim vs `app.*` spec) and never
 * cross-gate.
 */
export const MCP_APPS_FULL_SURFACE: ResolvedMcpAppsCapabilities =
  MCP_APPS_FULL as ResolvedMcpAppsCapabilities;

export const MCP_APPS_CLAUDE_SURFACE: ResolvedMcpAppsCapabilities =
  MCP_APPS_CLAUDE as ResolvedMcpAppsCapabilities;

export const MCP_APPS_CHATGPT_SURFACE: ResolvedMcpAppsCapabilities =
  MCP_APPS_CHATGPT as ResolvedMcpAppsCapabilities;

export const MCP_APPS_CURSOR_SURFACE: ResolvedMcpAppsCapabilities =
  MCP_APPS_CURSOR as ResolvedMcpAppsCapabilities;

/**
 * Spec-default "no claims" surface — every advertise key off, no
 * notification gates, no behavior gates. `availableDisplayModes` stays
 * `["inline"]` (the spec baseline; an empty array is not a valid
 * resolved value). Used by the resolver as the fallback for unknown /
 * unrecognized host styles so persisted `mcpAppsOverrides` cannot
 * silently advertise near-full support on hosts that don't exist
 * (mirrors `SPEC_DEFAULT_HOST_CAPABILITIES` in `registry.ts`).
 */
export const MCP_APPS_NO_CLAIMS_SURFACE: ResolvedMcpAppsCapabilities =
  MCP_APPS_NO_CLAIMS as ResolvedMcpAppsCapabilities;

/**
 * Microsoft 365 Copilot's published MCP Apps spec-bridge surface, verbatim
 * from the "Supported MCP Apps capabilities in Copilot" → "Component
 * bridge" table at
 * https://learn.microsoft.com/en-us/microsoft-365/copilot/extensibility/plugin-mcp-apps
 *
 * Diffs from FULL:
 *   - `availableDisplayModes` is `["inline", "fullscreen"]` (no `pip`).
 *     Copilot renders widgets INLINE by default and supports fullscreen as
 *     a mode the widget can request. The docs phrase
 *     `requestDisplayMode` as supported "fullscreen only" — that means
 *     fullscreen is the only expansion a widget may request, NOT that
 *     inline is unavailable (the docs show an inline widget screenshot and
 *     a user-clicked "Enter Fullscreen" button). Advertising
 *     `["fullscreen"]` alone made the clamp coerce the initial mode to
 *     fullscreen and trap the widget there (the close button could never
 *     return to inline).
 *   - `toolInputPartial`, `toolCancelled`, `hostContextChanged`,
 *     `resourceTeardown` off — these `ui/notifications/*` are not
 *     delivered by Copilot.
 *   - `toolInfo` off — `app.getHostContext()?.toolInfo` is not provided.
 *   - `serverResources`, `logging` off — Copilot does not advertise these
 *     `HostCapabilities` keys.
 *   - Sandbox `permissions`, `frameDomains`, `baseUriDomains` off —
 *     Copilot does not honor those resource `_meta.ui` sub-fields.
 *   - `resourcePrefersBorder` off — Copilot does not honor
 *     `_meta.ui.prefersBorder`.
 *
 * Note: `updateModelContext` and `message` stay on (Copilot honors both).
 */
export const MCP_APPS_COPILOT_SURFACE: ResolvedMcpAppsCapabilities =
  MCP_APPS_COPILOT as ResolvedMcpAppsCapabilities;

/**
 * Goose Desktop 1.38.0 captured MCP Apps surface. Goose renders MCP Apps and
 * exposes a rich HostContext (theme, display modes, style variables), but the
 * captured `ui/initialize.hostCapabilities` only advertised `openLinks`.
 * Keep the rest off until a probe demonstrates those bridge methods.
 */
export const MCP_APPS_GOOSE_SURFACE: ResolvedMcpAppsCapabilities =
  MCP_APPS_GOOSE as ResolvedMcpAppsCapabilities;

/**
 * Slackbot MCP host surface captured on 2026-06-24. Slackbot renders MCP Apps and
 * exposes HostContext/tool notifications, but the probed `ui/initialize`
 * hostCapabilities advertised only openLinks, serverTools, serverResources,
 * and logging. No `window.openai` surface was present in the iframe.
 */
export const MCP_APPS_SLACK_SURFACE: ResolvedMcpAppsCapabilities = {
  availableDisplayModes: ["inline", "fullscreen"],
  toolInputPartial: false,
  toolCancelled: false,
  hostContextChanged: false,
  resourceTeardown: false,
  toolInfo: true,
  openLinks: true,
  serverTools: true,
  serverResources: true,
  logging: true,
  updateModelContext: false,
  message: false,
  sandboxPermissions: false,
  cspFrameDomains: false,
  cspBaseUriDomains: false,
  resourcePrefersBorder: false,
  downloadFile: false,
  requestTeardown: false,
  widgetDisplayModeRequests: "accept",
};

// NOTE: capability presets are best-effort mocks of what each vendor publicly
// supports today. Treat them as starting points — verify against vendor docs
// when behavior matters, and refine as the inspector's enforcement layer
// (Step 4) lands. Sandbox is omitted intentionally; it's resource-derived at
// runtime (see HostMcpProfile.hostCapabilities).
export const CLAUDE_HOST_STYLE: HostStyleDefinition = {
  id: "claude",
  mcp: {
    protocolOverride: UIType.MCP_APPS,
    platform: CLAUDE_DESKTOP_PLATFORM,
    fontCss: CLAUDE_DESKTOP_FONT_CSS,
    // Claude advertises the full MCP Apps spec-bridge surface. `openLinks`
    // and `serverTools` are fixed-on baseline (not in matrix);
    // serverResources / logging / updateModelContext / message are matrix-
    // controlled and all on. listChanged sub-fields stay omitted because
    // the renderer doesn't forward those notifications yet — apps that
    // gate on `listChanged: true` would otherwise hit dead paths.
    mcpAppsCapabilities: MCP_APPS_CLAUDE_SURFACE,
    resolveStyleVariables: getClaudeDesktopStyleVariables,
  },
  chatUi: {
    label: "Claude",
    shortLabel: "Claude-style host",
    pickerDescription: "Claude-style swarm chrome",
    logoSrc: claudeLogo,
    family: "claude",
    resolveChatBackground: (theme) => CLAUDE_DESKTOP_CHAT_BACKGROUND[theme],
    loadingIndicator: ClaudeMarkIndicator,
  },
};

// Claude Code is a terminal agent with no chat chrome of its own, so it
// borrows Claude's desktop chat surface wholesale (style variables, fonts,
// background, MCP profile) and only differs in brand identity: its own
// label, logo, and a CLI spinner busy-state instead of the claude.ai
// mascot. Mirrors how CODEX_HOST_STYLE borrows ChatGPT's surface.
//
// Capabilities reuse Claude's preset here, but the catalog host definition
// overrides host app capabilities since the CLI renders no MCP Apps — the style
// preset is just the fallback if a host ever clears that override.
export const CLAUDE_CODE_HOST_STYLE: HostStyleDefinition = {
  id: "claude-code",
  mcp: {
    protocolOverride: UIType.MCP_APPS,
    platform: CLAUDE_DESKTOP_PLATFORM,
    fontCss: CLAUDE_DESKTOP_FONT_CSS,
    mcpAppsCapabilities: MCP_APPS_FULL_SURFACE,
    resolveStyleVariables: getClaudeDesktopStyleVariables,
  },
  chatUi: {
    label: "Claude Code",
    shortLabel: "Claude Code-style host",
    pickerDescription: "Anthropic Claude Code CLI chrome",
    logoSrc: claudeCodeLogo,
    family: "claude",
    resolveChatBackground: (theme) => CLAUDE_DESKTOP_CHAT_BACKGROUND[theme],
    loadingIndicator: ClaudeCodeCliIndicator,
  },
};

export const CHATGPT_HOST_STYLE: HostStyleDefinition = {
  id: "chatgpt",
  mcp: {
    protocolOverride: UIType.OPENAI_SDK,
    platform: CHATGPT_PLATFORM,
    fontCss: CHATGPT_FONT_CSS,
    mcpAppsCapabilities: MCP_APPS_CHATGPT_SURFACE,
    resolveStyleVariables: getChatGPTStyleVariables,
    // Real ChatGPT exposes the OpenAI Apps SDK `window.openai` surface
    // to widget HTML; emulating it here keeps existing Apps SDK widgets
    // rendering as their authors intended. Per-method capabilities = the
    // full surface (every method on, requestDisplayMode unconstrained).
    compatRuntime: {
      openaiApps: true,
      openaiAppsCapabilities: OPENAI_APPS_FULL_SURFACE,
    },
  },
  chatUi: {
    label: "ChatGPT",
    shortLabel: "ChatGPT-style host",
    pickerDescription: "OpenAI-style swarm chrome",
    logoSrc: openaiLogo,
    family: "chatgpt",
    resolveChatBackground: (theme) => CHATGPT_CHAT_BACKGROUND[theme],
    loadingIndicator: ChatGptDotIndicator,
  },
};

/**
 * Mistral Le Chat host style. Le Chat is a real MCP Apps host — it renders
 * widgets through `ui/initialize`. One capture reported base MCP
 * `clientCapabilities: {}` despite rendering Apps; the template normalizes
 * that to the standard `io.modelcontextprotocol/ui` advertisement while this
 * style keeps the Apps-side host surface faithful to the captured behavior.
 *
 * Style variables are captured from Le Chat's `host-context-changed` payload;
 * the chat surface is Le Chat's near-black (#111115) dark / white light. The
 * loading indicator is Le Chat's iconic morphing five-square loader plus a
 * shimmering "Thinking" label (see `indicators/mistral-spinner.tsx`).
 */
export const MISTRAL_HOST_STYLE: HostStyleDefinition = {
  id: "mistral",
  mcp: {
    protocolOverride: UIType.MCP_APPS,
    platform: MISTRAL_PLATFORM,
    fontCss: MISTRAL_FONT_CSS,
    // Keep this preset keyed to the Apps-side `ui/initialize` evidence:
    // no PIP, no downloadFile, no teardown claims, no `window.openai` shim,
    // and a text+image message surface.
    mcpAppsCapabilities: {
      ...MCP_APPS_FULL_SURFACE,
      availableDisplayModes: ["inline", "fullscreen"],
      toolCancelled: false,
      resourceTeardown: false,
      toolInfo: false,
      cspFrameDomains: false,
      cspBaseUriDomains: false,
      resourcePrefersBorder: false,
      downloadFile: false,
      requestTeardown: false,
      widgetDisplayModeRequests: "accept",
    },
    hostCapabilitiesAugment: {
      message: { image: {} },
    },
    resolveStyleVariables: getMistralStyleVariables,
  },
  chatUi: {
    label: "Mistral",
    shortLabel: "Mistral-style host",
    pickerDescription: "Mistral web host",
    logoSrc: mistralLogo,
    family: "chatgpt",
    resolveChatBackground: (theme) => MISTRAL_CHAT_BACKGROUND[theme],
    loadingIndicator: MistralSpinnerIndicator,
  },
};

/**
 * Goose Desktop host style. Captured from Goose 1.38.0: base MCP advertises
 * `io.modelcontextprotocol/ui`, the iframe completes `ui/initialize`, and the
 * host provides Cash Sans style variables. It does not expose `window.openai`,
 * so Apps SDK widgets need an MCP Apps bridge or text fallback.
 */
export const GOOSE_HOST_STYLE: HostStyleDefinition = {
  id: "goose",
  mcp: {
    protocolOverride: UIType.MCP_APPS,
    platform: GOOSE_PLATFORM,
    fontCss: GOOSE_FONT_CSS,
    mcpAppsCapabilities: MCP_APPS_GOOSE_SURFACE,
    resolveStyleVariables: getGooseStyleVariables,
  },
  chatUi: {
    label: "Goose",
    shortLabel: "Goose-style host",
    pickerDescription: "Goose Desktop host",
    logoSrc: gooseLogoLight,
    logoSrcByTheme: {
      light: gooseLogoLight,
      dark: gooseLogoDark,
    },
    family: "chatgpt",
    resolveChatBackground: (theme) => GOOSE_CHAT_BACKGROUND[theme],
    loadingIndicator: GooseIconIndicator,
  },
};

/**
 * Slackbot host style. Captured from the Slackbot MCP host: base MCP advertises
 * `io.modelcontextprotocol/ui`, the iframe completes `ui/initialize`, and
 * the host provides Slack-Lato style variables. It does not expose
 * `window.openai`, so Apps SDK widgets need an MCP Apps bridge or fallback.
 */
export const SLACK_HOST_STYLE: HostStyleDefinition = {
  id: "slack",
  mcp: {
    protocolOverride: UIType.MCP_APPS,
    platform: SLACK_PLATFORM,
    fontCss: SLACK_FONT_CSS,
    mcpAppsCapabilities: MCP_APPS_SLACK_SURFACE,
    resolveStyleVariables: getSlackStyleVariables,
  },
  chatUi: {
    label: "Slackbot",
    shortLabel: "Slackbot-style host",
    pickerDescription: "Slackbot MCP host",
    logoSrc: slackLogo,
    family: "chatgpt",
    resolveChatBackground: (theme) => SLACK_CHAT_BACKGROUND[theme],
    loadingIndicator: CodexShineIndicator,
  },
};

export const CURSOR_HOST_STYLE: HostStyleDefinition = {
  id: "cursor",
  mcp: {
    // Cursor advertises only `text/html;profile=mcp-app` (per probe
    // clientCapabilities.extensions["io.modelcontextprotocol/ui"]).
    protocolOverride: UIType.MCP_APPS,
    platform: CURSOR_PLATFORM,
    fontCss: CURSOR_FONT_CSS,
    // Matrix captured verbatim from a Cursor 3.4.17 probe. Notably Cursor
    // does NOT advertise `updateModelContext` or `message`. The
    // `listChanged: false` markers on serverTools/serverResources don't
    // fit the M365-grain matrix (which is advertise-or-not booleans), so
    // they're carried as a preset-only `hostCapabilitiesAugment` below.
    // Don't widen without evidence — apps that gate on `listChanged: true`
    // need to know real Cursor doesn't send them.
    mcpAppsCapabilities: MCP_APPS_CURSOR_SURFACE,
    hostCapabilitiesAugment: {
      serverTools: { listChanged: false },
      serverResources: { listChanged: false },
    },
    resolveStyleVariables: getCursorStyleVariables,
  },
  chatUi: {
    label: "Cursor",
    shortLabel: "Cursor-style host",
    pickerDescription: "Cursor IDE chat panel chrome",
    logoSrc: cursorLogo,
    // Visual family: Cursor's chat panel is a dark, flat, IDE-like surface
    // — closer to ChatGPT than to Claude's warm bubbles. Routes
    // family-keyed branches (bubble shape, send hint, etc.) to the
    // chatgpt visual until Cursor earns its own family.
    family: "chatgpt",
    resolveChatBackground: (theme) => CURSOR_CHAT_BACKGROUND[theme],
    loadingIndicator: CursorShineIndicator,
  },
};

/**
 * Microsoft 365 Copilot host style. Reuses ChatGPT's MCP profile and most
 * of its chat chrome — Copilot routes widgets through the OpenAI Apps SDK
 * under the hood and its chat UI sits in the same flat-neutral visual
 * bucket. Only the label, picker description, logo, chat background, and
 * loading indicator are Copilot-specific. The indicator is a faithful
 * recreation of M365 Copilot's 3-circle gradient pulse (see
 * `indicators/copilot-pulse.tsx`).
 */
export const COPILOT_HOST_STYLE: HostStyleDefinition = {
  id: "copilot",
  mcp: {
    protocolOverride: UIType.OPENAI_SDK,
    platform: CHATGPT_PLATFORM,
    fontCss: CHATGPT_FONT_CSS,
    // Microsoft 365 Copilot's published MCP Apps subset (see
    // MCP_APPS_COPILOT_SURFACE for the per-row M365 table mapping).
    // Strips serverResources / logging / notification gates / sandbox
    // sub-fields / resource prefersBorder; clamps display modes to
    // fullscreen-only.
    mcpAppsCapabilities: MCP_APPS_COPILOT_SURFACE,
    resolveStyleVariables: getChatGPTStyleVariables,
    // Copilot routes widgets through the OpenAI Apps SDK under the
    // hood, but exposes only a subset of `window.openai.*` — see
    // OPENAI_APPS_COPILOT_SURFACE for the per-method matrix.
    compatRuntime: {
      openaiApps: true,
      openaiAppsCapabilities: OPENAI_APPS_COPILOT_SURFACE,
    },
  },
  chatUi: {
    label: "Copilot",
    shortLabel: "Copilot-style host",
    pickerDescription: "Microsoft 365 Copilot chrome",
    logoSrc: copilotLogo,
    family: "chatgpt",
    // Light surface mirrors ChatGPT (pure white). Dark surface is
    // Copilot's slightly lighter neutral (#303030) — distinct from
    // ChatGPT's #212121, captured from M365 Copilot's chat panel.
    resolveChatBackground: (theme) =>
      theme === "dark" ? "rgba(48, 48, 48, 1)" : CHATGPT_CHAT_BACKGROUND.light,
    loadingIndicator: CopilotPulseIndicator,
  },
};

/**
 * OpenAI Codex host style. Codex itself is a CLI tool (no widget
 * rendering — see the Codex catalog host definition, which
 * advertises `elicitation`-only client capabilities), so this entry is
 * a playground stand-in rather than a faithful clone of a real Codex
 * surface. We mirror ChatGPT's MCP profile because Codex is OpenAI-
 * flavored: if a widget ever did land in a Codex-adjacent surface, the
 * OpenAI Apps SDK is the right protocol bucket. In practice the `mcp`
 * blob is unread (real Codex never renders an iframe).
 *
 * Chat surface reuses ChatGPT's `#212121` dark / white light colors
 * verbatim — Codex doesn't have its own published chat chrome to copy,
 * and ChatGPT's neutral palette is the closest analog to a terminal-
 * adjacent OpenAI tool. The loading indicator is the shimmering
 * "Thinking" treatment (`CodexShineIndicator`), which shares CSS with
 * Cursor's shine via a multi-selector rule in `index.css`.
 */
export const CODEX_HOST_STYLE: HostStyleDefinition = {
  id: "codex",
  mcp: {
    protocolOverride: UIType.OPENAI_SDK,
    platform: CHATGPT_PLATFORM,
    fontCss: CHATGPT_FONT_CSS,
    // Codex shares ChatGPT's matrix (CLI surface mostly unused, but the
    // OpenAI-flavored protocol bucket carries over). serverResources /
    // logging off; updateModelContext / message on.
    mcpAppsCapabilities: {
      ...MCP_APPS_FULL_SURFACE,
      serverResources: false,
      logging: false,
      // Same CSP answers as ChatGPT (2026-08-19 probe), same app runtime.
      // `connect-src` is one directive, so the three subtypes cannot diverge:
      // the declared wss endpoint connected while an undeclared one took a
      // real violation, so the declared list IS honored. Resource subtypes
      // stay unknown — the probe's declared origin sits in ChatGPT's own
      // baseline allowlist, so it proves nothing either way.
      cspConnectDomains: { fetch: true, xhr: true, websocket: true },
    },
    resolveStyleVariables: getChatGPTStyleVariables,
    // Codex is a CLI (no widget rendering surface), so the `window.openai`
    // shim is moot in practice. Keep it off so the inspector's emulated
    // Codex doesn't lie about a surface real Codex doesn't expose.
  },
  chatUi: {
    label: "Codex",
    shortLabel: "Codex-style host",
    pickerDescription: "OpenAI Codex CLI-style chrome",
    logoSrc: codexLogo,
    family: "chatgpt",
    resolveChatBackground: (theme) => CHATGPT_CHAT_BACKGROUND[theme],
    loadingIndicator: CodexShineIndicator,
  },
};

/**
 * Visual Studio Code 1.130.0 host style, captured from a live MCP base
 * initialize + MCP Apps ui/initialize probe on 2026-07-23. VS Code supplies
 * semantic `var(--vscode-...)` references; the resolver uses the corresponding
 * browser-resolved light and dark captures outside the VS Code workbench.
 */
export const VSCODE_HOST_STYLE: HostStyleDefinition = {
  id: "vscode",
  mcp: {
    protocolOverride: UIType.MCP_APPS,
    platform: VSCODE_PLATFORM,
    fontCss: VSCODE_FONT_CSS,
    mcpAppsCapabilities: MCP_APPS_VSCODE as ResolvedMcpAppsCapabilities,
    hostCapabilitiesAugment: {
      serverTools: { listChanged: true },
      serverResources: { listChanged: true },
    },
    // The matrix's generic updateModelContext default is `{ text: {} }`.
    // VS Code's capture advertises this exact typed set instead, with no text.
    hostCapabilitiesReplacement: {
      updateModelContext: {
        audio: {},
        image: {},
        resourceLink: {},
        resource: {},
        structuredContent: {},
      },
    },
    resolveStyleVariables: getVscodeStyleVariables,
  },
  chatUi: {
    label: "VS Code",
    shortLabel: "VS Code-style host",
    pickerDescription: "Visual Studio Code chat panel chrome",
    logoSrc: vscodeLogo,
    // Flat, dark, IDE-like surface — same visual family as Cursor/ChatGPT.
    family: "chatgpt",
    resolveChatBackground: (theme) => VSCODE_CHAT_BACKGROUND[theme],
    loadingIndicator: CursorShineIndicator,
  },
};

/**
 * AWS Bedrock AgentCore host style. AgentCore is a server-side agent
 * runtime that permits only text-based MCP servers — it does not render
 * MCP Apps widgets (analogous to the Codex CLI; see the AgentCore catalog host
 * definition, which advertises `elicitation`-only client
 * capabilities). This entry is therefore a playground stand-in, not a
 * faithful clone of a real rendering surface.
 *
 * Chrome reuses Sphinx's neutral house tokens — AgentCore has no published
 * chat UI of its own to copy, and the neutral surface is the honest choice
 * (don't invent AWS-branded chrome). The capability surface is the
 * spec-default "no claims" set because AgentCore renders nothing; the `mcp`
 * blob is unread in practice (no iframe is ever created).
 */
export const AGENTCORE_HOST_STYLE: HostStyleDefinition = {
  id: "agentcore",
  mcp: {
    protocolOverride: UIType.MCP_APPS,
    platform: SPHINX_PLATFORM,
    fontCss: SPHINX_FONT_CSS,
    // No widget rendering → advertise nothing. Honest baseline for a
    // text-only host.
    mcpAppsCapabilities: MCP_APPS_NO_CLAIMS_SURFACE,
    resolveStyleVariables: getSphinxStyleVariables,
  },
  chatUi: {
    label: "AgentCore",
    shortLabel: "AgentCore-style host",
    pickerDescription: "AWS Bedrock AgentCore runtime (text servers only)",
    logoSrc: bedrockLogo,
    // Maps onto the claude visual family (warm bubble chat language) like
    // Sphinx, whose neutral tokens AgentCore borrows.
    family: "claude",
    resolveChatBackground: (theme) => SPHINX_CHAT_BACKGROUND[theme],
    // AgentCore is a text-only AWS runtime with no chat surface of its own, so
    // it shows the generic Codex/Cursor "Thinking" shimmer rather than a
    // branded mark. (Its "claude" family is for bubble chrome only — it opts
    // out of the claude.ai streaming-footer mark in loading-indicator-content.)
    loadingIndicator: CodexShineIndicator,
  },
};

/**
 * n8n MCP Client Tool host style. The real n8n client is a workflow-node
 * tool caller with no MCP Apps rendering surface, so its MCP matrix stays
 * at the no-claims baseline. The chat chrome is just Sphinx's neutral
 * stand-in with n8n identity; it exists so users can run the same simulated
 * turns through an n8n-shaped MCP initialize profile.
 */
export const N8N_HOST_STYLE: HostStyleDefinition = {
  id: "n8n",
  mcp: {
    protocolOverride: UIType.MCP_APPS,
    platform: SPHINX_PLATFORM,
    fontCss: SPHINX_FONT_CSS,
    mcpAppsCapabilities: MCP_APPS_NO_CLAIMS_SURFACE,
    resolveStyleVariables: getSphinxStyleVariables,
  },
  chatUi: {
    label: "n8n",
    shortLabel: "n8n-style host",
    pickerDescription: "n8n MCP Client Tool (tools-only)",
    logoSrc: n8nLogo,
    family: "chatgpt",
    resolveChatBackground: (theme) => SPHINX_CHAT_BACKGROUND[theme],
    // Sphinx's three-dot wave recolored to n8n's coral brand red.
    loadingIndicator: N8nMarkIndicator,
  },
};

/**
 * Perplexity MCP client host style. The captured Perplexity probe exposes
 * only the base MCP initialize layer (`mcp@0.1.0`) with no capabilities and
 * no snapshot, so this preset mirrors a headless tool-calling client.
 */
export const PERPLEXITY_HOST_STYLE: HostStyleDefinition = {
  id: "perplexity",
  mcp: {
    protocolOverride: UIType.MCP_APPS,
    platform: SPHINX_PLATFORM,
    fontCss: SPHINX_FONT_CSS,
    mcpAppsCapabilities: MCP_APPS_NO_CLAIMS_SURFACE,
    resolveStyleVariables: getSphinxStyleVariables,
  },
  chatUi: {
    label: "Perplexity",
    shortLabel: "Perplexity-style host",
    pickerDescription: "Perplexity MCP client (tools-only)",
    logoSrc: perplexityLogo,
    family: "chatgpt",
    resolveChatBackground: (theme) => SPHINX_CHAT_BACKGROUND[theme],
    // Scrolling Perplexity-mark sprite strip (the mark morphing/rotating in
    // place), captured verbatim from a live perplexity.ai probe.
    loadingIndicator: PerplexityMarkIndicator,
  },
};

/**
 * Cline host style. Captured from a Cline 3.89.2 probe: protocol 2025-11-25,
 * an empty `clientCapabilities` object, and no uploaded snapshot — so Cline is
 * a bare, tools-only MCP consumer with no MCP Apps/UI extension and no widget
 * rendering surface. The MCP matrix stays at the no-claims baseline. The chat
 * chrome is Sphinx's neutral stand-in with Cline identity; there's no captured
 * thinking animation, so the busy state reuses the brand mark + "Thinking"
 * label (same shape as the Goose indicator).
 */
export const CLINE_HOST_STYLE: HostStyleDefinition = {
  id: "cline",
  mcp: {
    protocolOverride: UIType.MCP_APPS,
    platform: SPHINX_PLATFORM,
    fontCss: SPHINX_FONT_CSS,
    mcpAppsCapabilities: MCP_APPS_NO_CLAIMS_SURFACE,
    resolveStyleVariables: getSphinxStyleVariables,
  },
  chatUi: {
    label: "Cline",
    shortLabel: "Cline-style host",
    pickerDescription: "Cline MCP client (tools-only)",
    logoSrc: clineLogoLight,
    logoSrcByTheme: {
      light: clineLogoLight,
      dark: clineLogoDark,
    },
    family: "chatgpt",
    resolveChatBackground: (theme) => SPHINX_CHAT_BACKGROUND[theme],
    // No captured Cline thinking animation; reuse the brand mark + label.
    loadingIndicator: ClineMarkIndicator,
  },
};

/**
 * Notion AI agent host style. The Notion client is a bare, tools-only MCP
 * consumer (empty capabilities, no MCP Apps/UI extension), so its MCP matrix
 * stays at the no-claims baseline and there's no widget rendering surface.
 * The chat chrome is Sphinx's neutral stand-in with Notion identity; what
 * makes it feel like Notion is the shimmer "Working" thinking indicator
 * (captured from notion.so DevTools).
 */
export const NOTION_HOST_STYLE: HostStyleDefinition = {
  id: "notion",
  mcp: {
    protocolOverride: UIType.MCP_APPS,
    platform: SPHINX_PLATFORM,
    fontCss: SPHINX_FONT_CSS,
    mcpAppsCapabilities: MCP_APPS_NO_CLAIMS_SURFACE,
    resolveStyleVariables: getSphinxStyleVariables,
  },
  chatUi: {
    label: "Notion",
    shortLabel: "Notion-style host",
    pickerDescription: "Notion AI agent (tools-only)",
    logoSrc: notionLogo,
    family: "chatgpt",
    resolveChatBackground: (theme) => SPHINX_CHAT_BACKGROUND[theme],
    // Notion's shimmer-text "Working" label, ported from a live notion.so probe.
    loadingIndicator: NotionShimmerIndicator,
  },
};

/**
 * Sphinx's own house chrome. Used as the inspector's default host style so
 * "no host selected" doesn't silently render as Claude. Capability blob is
 * the inspector's actual MCP Apps renderer support — same baseline as
 * Claude minus `listChanged` notifications the renderer doesn't forward.
 */
export const SPHINX_HOST_STYLE: HostStyleDefinition = {
  id: "mcpjam",
  mcp: {
    protocolOverride: UIType.MCP_APPS,
    platform: SPHINX_PLATFORM,
    fontCss: SPHINX_FONT_CSS,
    // Sphinx is the inspector's own dev surface and intentionally
    // maximalist — full MCP Apps spec surface advertised so developers
    // testing here see every dimension a widget might touch.
    mcpAppsCapabilities: MCP_APPS_FULL_SURFACE,
    resolveStyleVariables: getSphinxStyleVariables,
    // Sphinx is the inspector's own house chrome and intentionally
    // maximalist: developers testing here should see the full
    // `window.openai` surface so widgets authored against OpenAI's
    // Apps SDK can be debugged in Sphinx without swapping to the
    // ChatGPT host. Real Sphinx exposes the shim deliberately (it's
    // not SEP-1865 honest, but it's the right call for a dev surface).
    compatRuntime: {
      openaiApps: true,
      openaiAppsCapabilities: OPENAI_APPS_FULL_SURFACE,
    },
  },
  chatUi: {
    label: "Sphinx",
    shortLabel: "Sphinx-style host",
    pickerDescription: "Sphinx house chrome",
    logoSrc: sphinxLogo,
    // Maps onto the claude visual family (warm bubble chat language) until
    // Sphinx earns its own. Family controls bubble shape, send hint, etc.;
    // colors and the loading mark are already Sphinx-branded above.
    family: "claude",
    resolveChatBackground: (theme) => SPHINX_CHAT_BACKGROUND[theme],
    loadingIndicator: SphinxMarkIndicator,
  },
};

export const BUILT_IN_HOST_STYLES: readonly HostStyleDefinition[] = [
  SPHINX_HOST_STYLE,
  CLAUDE_HOST_STYLE,
  CHATGPT_HOST_STYLE,
  MISTRAL_HOST_STYLE,
  GOOSE_HOST_STYLE,
  SLACK_HOST_STYLE,
  CURSOR_HOST_STYLE,
  COPILOT_HOST_STYLE,
  CODEX_HOST_STYLE,
  CLAUDE_CODE_HOST_STYLE,
  VSCODE_HOST_STYLE,
  AGENTCORE_HOST_STYLE,
  N8N_HOST_STYLE,
  PERPLEXITY_HOST_STYLE,
  CLINE_HOST_STYLE,
  NOTION_HOST_STYLE,
];
